import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { energyService } from '../../services/energyService';
import { formatUploadedAt } from '../../utils/datetime';
import { channelLabel, stepLabel } from './energyLabels';
import { logger } from '../../utils/logger';

// ─────────────────────────────────────────────────────────────────────────
// Level 3 — the atomic ledger: every individual charge, newest first.
//
// A REASONING item breaks its cost into input / thinking / output, and
// THINKING is called out as its own line: it is the component users cannot see
// in their own transcript, so it is the one they most need itemised.
// ─────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

// The three sub-components of a reasoning charge. `pts_*` is frozen by the
// contract; the matching TOKEN counts are not, so each is looked up across the
// plausible spellings and simply omitted when the BE sends none — a missing
// token count degrades the line, it never blanks the cost.
const REASONING_PARTS = [
    { ptsKey: 'pts_input', tokenKeys: ['input_tokens', 'tokens_input', 'prompt_tokens'], i18n: 'energyInputTokens', fallback: 'Input Tokens' },
    { ptsKey: 'pts_thinking', tokenKeys: ['thinking_tokens', 'tokens_thinking'], i18n: 'energyThinkingTokens', fallback: 'Thinking Tokens' },
    { ptsKey: 'pts_output', tokenKeys: ['output_tokens', 'tokens_output', 'completion_tokens'], i18n: 'energyOutputTokens', fallback: 'Output Tokens' },
];

const firstNumber = (detail, keys) => {
    for (const k of keys) {
        const n = Number(detail?.[k]);
        if (Number.isFinite(n)) return n;
    }
    return null;
};

// A SIGNED delta is coloured; an unsigned magnitude is not. The pillar
// endpoints report consumption as a magnitude, so inventing a red minus here
// would be a claim the payload never made — the tint only ever follows a sign
// the BE actually sent.
const deltaClass = (value) => {
    const s = String(value ?? '').trim();
    if (s.startsWith('-') || s.startsWith('−')) return 'text-pai-red';
    if (s.startsWith('+')) return 'text-pai-green';
    return 'text-white';
};

const ReasoningSubLines = ({ detail, t }) => {
    const parts = REASONING_PARTS.filter((p) => detail?.[p.ptsKey] != null);
    if (parts.length === 0) return null;
    return (
        <ul className="mt-1.5 space-y-0.5 pl-3 border-l border-pai-hairline min-w-0">
            {parts.map((p) => {
                const tokens = firstNumber(detail, p.tokenKeys);
                const label = t(p.i18n, p.fallback);
                return (
                    <li
                        key={p.ptsKey}
                        data-testid={`energy-item-${p.ptsKey}`}
                        className="text-[12px] text-pai-meta break-words min-w-0 tnum"
                    >
                        {tokens != null
                            ? `${label}: ${tokens.toLocaleString()} — ${detail[p.ptsKey]} ${t('energyPtsSuffix', 'pts')}`
                            : `${label}: ${detail[p.ptsKey]} ${t('energyPtsSuffix', 'pts')}`}
                    </li>
                );
            })}
        </ul>
    );
};

// Global Source Attribution (#315) — WHERE a charge came from.
//
// Rendered only for what the BE actually sent. A row with no conversation (a
// storage fee, a deposit) carries neither key, and that ABSENCE is the message:
// it is not conversation-scoped. Inventing "Unknown" would turn a fact into a
// defect report.
const OriginLine = ({ item, t }) => {
    const channel = item?.channel;
    const title = item?.conversation_title;
    if (!channel && !title) return null;
    return (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0 max-w-full">
            {channel && (
                <span
                    data-testid="energy-item-channel"
                    data-channel={channel}
                    className="text-[10.5px] font-semibold px-1.5 py-px rounded-chip border border-pai-subtle bg-white/[0.05] text-pai-secondary flex-shrink-0"
                >
                    {channelLabel(t, channel, item?.channel_label)}
                </span>
            )}
            {title && (
                <span
                    data-testid="energy-item-conversation"
                    title={title}
                    className="text-[12px] text-pai-meta truncate min-w-0"
                >
                    {t('energyFromConversation', 'From conversation: {title}', { title })}
                </span>
            )}
        </div>
    );
};

const AtomicList = ({ pillar, category, range }) => {
    const { t } = useLanguage();
    const [items, setItems] = useState([]);
    const [totalPts, setTotalPts] = useState(null);
    const [nextOffset, setNextOffset] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    const reqRef = useRef(0);

    const fetchPage = useCallback(async (offset, ticket) => {
        const res = await energyService.getPillarItems(pillar, {
            category,
            startDate: range?.startDate,
            endDate: range?.endDate,
            limit: PAGE_SIZE,
            offset,
        });
        if (ticket !== reqRef.current) return null;
        return res;
    }, [pillar, category, range]);

    useEffect(() => {
        const ticket = ++reqRef.current;
        let alive = true;
        setLoading(true);
        setError(null);
        setItems([]);
        setNextOffset(null);
        fetchPage(0, ticket)
            .then((res) => {
                if (!alive || res === null || ticket !== reqRef.current) return;
                setItems(res?.items || []);
                setTotalPts(res?.total_pts ?? null);
                setNextOffset(res?.next_offset ?? null);
                setLoading(false);
            })
            .catch((err) => {
                if (!alive || ticket !== reqRef.current) return;
                logger.warn('ENERGY', `pillar items failed: ${err?.message || err}`);
                setError(err);
                setLoading(false);
            });
        return () => { alive = false; };
    }, [fetchPage]);

    const loadMore = async () => {
        if (nextOffset == null || loadingMore) return;
        const ticket = reqRef.current;
        setLoadingMore(true);
        try {
            const res = await fetchPage(nextOffset, ticket);
            if (res === null) return;                       // superseded — drop it
            setItems((prev) => [...prev, ...(res?.items || [])]);
            setNextOffset(res?.next_offset ?? null);
        } catch (err) {
            logger.warn('ENERGY', `pillar items page failed: ${err?.message || err}`);
        } finally {
            // Cleared unconditionally: it is a local button flag, and leaving it
            // set on a superseded page would permanently disable "Load more".
            setLoadingMore(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 p-4 text-[13px] text-pai-meta">
                <Loader2 size={14} className="animate-spin" />
                {t('energyLoading', 'Loading…')}
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-[13px] text-pai-red break-words" data-testid="energy-level3-error">
                {t('energyError', "Couldn't load your energy data.")}
            </div>
        );
    }

    return (
        <section data-testid="energy-level3" className="min-w-0 max-w-full">
            <header className="flex items-center justify-between gap-2 mb-2.5 min-w-0">
                <span className="pai-eyebrow truncate min-w-0">
                    {t('energyItems', 'Activity')}
                </span>
                {totalPts != null && (
                    <span
                        data-testid="energy-level3-total"
                        className="text-[13.5px] font-bold text-white tnum flex-shrink-0"
                    >
                        {totalPts}
                    </span>
                )}
            </header>

            {items.length === 0 ? (
                <p data-testid="energy-level3-empty" className="text-[13px] text-pai-meta break-words">
                    {t('energyNoItems', 'Nothing recorded here for the selected range.')}
                </p>
            ) : (
                // The ledger: divided rows rather than stacked cards, so a long
                // run of charges reads as one statement.
                <ul className="min-w-0 border-t border-pai-hairline">
                    {items.map((item, i) => (
                        <li
                            key={item?.id ?? `${item?.at}-${i}`}
                            data-testid="energy-item"
                            className="px-1 py-3 border-b border-pai-hairline last:border-0 min-w-0 max-w-full"
                        >
                            <div className="flex items-baseline gap-3 min-w-0">
                                <span className="text-[15px] font-semibold text-white truncate min-w-0 flex-1">
                                    {stepLabel(t, item?.pipeline_step)}
                                </span>
                                <span className="text-[13px] text-pai-meta tnum flex-shrink-0">
                                    {formatUploadedAt(item?.at) || '—'}
                                </span>
                                {/* Verbatim 2dp string, right-aligned in a fixed
                                    column so the deltas line up down the page. */}
                                <span
                                    className={`text-[15px] font-bold tnum flex-shrink-0 text-right min-w-[78px] ${deltaClass(item?.pts)}`}
                                >
                                    {item?.pts ?? '0.00'}
                                </span>
                            </div>
                            <OriginLine item={item} t={t} />
                            <ReasoningSubLines detail={item?.detail} t={t} />
                        </li>
                    ))}
                </ul>
            )}

            {nextOffset != null && (
                <button
                    type="button"
                    data-testid="energy-load-more"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="pai-btn-ghost mt-3 w-full disabled:opacity-60"
                >
                    {loadingMore ? t('energyLoading', 'Loading…') : t('energyLoadMore', 'Load more')}
                </button>
            )}
        </section>
    );
};

export default AtomicList;
